import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";

interface BusinessGuardProps {
  children: ReactNode;
}

/**
 * Route guard that prevents Super Admins from accessing business-scoped routes.
 * Must be used INSIDE ProtectedRoute (auth already checked).
 * Super Admins should use /platform/* routes instead.
 */
export function BusinessGuard({ children }: BusinessGuardProps) {
  const { isSuperAdmin, isOrgAdmin, businessMemberships, activeBusiness, loading, signOut, needsFirmSelection } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Super Admins should not access business routes
  if (isSuperAdmin) {
    return <Navigate to="/platform" replace />;
  }

  if (isOrgAdmin && businessMemberships.length === 0) {
    return <Navigate to="/firm/onboarding" replace />;
  }

  // Transient context gap: user has memberships but active business is still resolving.
  // Keep user on current route with loading UI instead of redirecting to home.
  // Skip if needsFirmSelection — those users should redirect, not spin forever.
  if (!isOrgAdmin && !activeBusiness && businessMemberships.length > 0 && !needsFirmSelection) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Multi-org user whose current org has no businesses — redirect to firm
  // selection so they can switch to an org where they DO have access.
  if (!isOrgAdmin && !activeBusiness && needsFirmSelection) {
    return <Navigate to="/select-firm" replace />;
  }

  // Dead-end: user has an org membership but no business assignment and is not
  // an org owner. Redirecting to "/" would create a loop since HomeRedirect
  // sends non-owner users back to "/dashboard" → BusinessGuard → "/" again.
  // Show an informational message instead.
  if (!isOrgAdmin && !activeBusiness) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md mx-4 text-center space-y-4">
          <h2 className="text-xl font-semibold">No Business Assigned</h2>
          <p className="text-muted-foreground">
            Your account is not assigned to any business in this firm.
            Contact your firm administrator for access.
          </p>
          <button
            onClick={() => signOut()}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
